import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Building2,
  Users as UsersIcon,
  CarFront,
  KeyRound,
  Power,
  Trash2,
  Shield,
  Search,
  Sparkles,
  BadgeDollarSign,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { api, apiErrorMessage } from "../lib/api";
import { dateTR, numberTR, currencyTRY } from "../lib/format";
import { useAuth } from "../lib/auth";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div
      className="rounded-2xl border border-stone-200 bg-white p-5"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-stone-500">
            {label}
          </div>
          <div className="mt-2 font-display font-black text-3xl text-slate-900 num-tabular">
            {value}
          </div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-stone-100 text-slate-700 flex items-center justify-center">
          <Icon className="w-5 h-5" strokeWidth={1.8} />
        </div>
      </div>
    </div>
  );
}

function ResetDialog({ user, onClose, onDone }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/portal/users/${user.id}/reset-password`, {
        password: pwd,
      });
      toast.success(`${user.name} için yeni şifre atandı`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Şifre Sıfırla</DialogTitle>
          <DialogDescription>
            <b>{user.name}</b> ({user.email}) için yeni şifre belirleyin.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="text"
            required
            minLength={6}
            autoFocus
            data-testid="portal-reset-pwd"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className="w-full h-11 px-3.5 rounded-xl border border-stone-300 focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/20"
            placeholder="En az 6 karakter"
          />
          <DialogFooter>
            <button
              type="submit"
              disabled={busy}
              data-testid="portal-reset-submit"
              className="h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm disabled:opacity-60"
            >
              {busy ? "Kaydediliyor…" : "Şifreyi Kaydet"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Portal() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState("");
  const [q, setQ] = useState("");
  const [resetTarget, setResetTarget] = useState(null);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/portal/summary"),
        api.get("/portal/users"),
      ]);
      setSummary(s.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (companyFilter && u.company_id !== companyFilter) return false;
      if (q) {
        const s = q.toLowerCase();
        const hay = `${u.name} ${u.email} ${u.company_name}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [users, companyFilter, q]);

  const toggleStatus = async (u) => {
    const next = u.status === "approved" ? "disabled" : "approved";
    const verb = next === "disabled" ? "devre dışı bırak" : "etkinleştir";
    if (!window.confirm(`${u.name} adlı üyeyi ${verb}mak istediğinize emin misiniz?`)) return;
    try {
      await api.post(`/portal/users/${u.id}/status`, { status: next });
      toast.success(next === "disabled" ? "Üye devre dışı bırakıldı" : "Üye etkinleştirildi");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const setRole = async (u, role) => {
    if (u.role === role) return;
    if (!window.confirm(`${u.name} adlı üyenin rolünü "${role === "admin" ? "Yönetici" : "Üye"}" olarak değiştirmek istediğinize emin misiniz?`))
      return;
    try {
      await api.post(`/portal/users/${u.id}/set-role`, { role });
      toast.success("Rol güncellendi");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`${u.name} hesabını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/portal/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const removeCompany = async (c) => {
    if (
      !window.confirm(
        `${c.name} firmasını ve TÜM verilerini (${c.user_count} kullanıcı, ${c.vehicle_count} araç, ${c.rental_count} kiralama) silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
      )
    )
      return;
    try {
      await api.delete(`/portal/companies/${c.id}`);
      toast.success("Firma silindi");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-80" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  return (
    <div data-testid="portal-page">
      <div className="mb-6">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500">
          Portal Yönetimi
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 mt-1">
          Tüm Firmalar & Üyeler
        </h1>
        <p className="text-stone-500 text-sm mt-2 max-w-2xl">
          Platformdaki tüm galerileri ve kullanıcıları buradan yönetin.
          Kullanıcıları etkin/pasif yapın, şifrelerini sıfırlayın veya
          hesapları silin.
        </p>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          testid="portal-stat-companies"
          label="Firma"
          value={numberTR(summary.totals.company_count)}
          icon={Building2}
        />
        <StatCard
          testid="portal-stat-users"
          label="Toplam Kullanıcı"
          value={numberTR(summary.totals.user_count)}
          icon={UsersIcon}
        />
        <StatCard
          testid="portal-stat-active"
          label="Aktif Kullanıcı"
          value={numberTR(summary.totals.active_user_count)}
          icon={Shield}
        />
        <StatCard
          testid="portal-stat-vehicles"
          label="Toplam Araç"
          value={numberTR(summary.totals.vehicle_count)}
          icon={CarFront}
        />
      </div>

      {/* Subscription revenue */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          testid="portal-stat-mrr"
          label="Aylık Gelir (MRR)"
          value={currencyTRY(summary.totals.monthly_recurring_revenue || 0)}
          icon={BadgeDollarSign}
        />
        <StatCard
          testid="portal-stat-active-subs"
          label="Aktif Abonelik"
          value={numberTR(summary.totals.active_subscriptions || 0)}
          icon={Sparkles}
        />
        <StatCard
          testid="portal-stat-trialing"
          label="Deneme Sürümünde"
          value={numberTR(summary.totals.trialing_subscriptions || 0)}
          icon={Clock}
        />
        <StatCard
          testid="portal-stat-expired"
          label="Süresi Dolan"
          value={numberTR(summary.totals.expired_subscriptions || 0)}
          icon={AlertTriangle}
        />
      </div>

      {/* Companies */}
      <div className="rounded-2xl border border-stone-200 bg-white mb-8 overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 bg-stone-50/60 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-stone-500" />
          <span className="text-[11px] font-semibold uppercase tracking-widest text-stone-500">
            Firmalar
          </span>
        </div>
        {summary.companies.length === 0 ? (
          <div className="p-10 text-center text-stone-500">Firma yok.</div>
        ) : (
          <div className="divide-y divide-stone-100">
            {summary.companies.map((c) => {
              const sub = c.subscription || {};
              const badgeTone =
                {
                  active: "bg-emerald-100 text-emerald-800",
                  trial: "bg-orange-100 text-orange-800",
                  expired: "bg-rose-100 text-rose-800",
                  trial_expired: "bg-rose-100 text-rose-800",
                  none: "bg-stone-100 text-stone-700",
                }[sub.status] || "bg-stone-100 text-stone-700";
              const badgeLabel =
                {
                  active: sub.plan
                    ? `${sub.plan[0].toUpperCase()}${sub.plan.slice(1)} · Aktif`
                    : "Aktif",
                  trial: `Deneme · ${sub.days_left ?? 0} gün`,
                  expired: "Süresi Doldu",
                  trial_expired: "Deneme Bitti",
                  none: "Abonelik Yok",
                }[sub.status] || "—";
              return (
              <div
                key={c.id}
                data-testid={`company-${c.id}`}
                className="px-5 py-4 flex flex-wrap items-center gap-4"
              >
                <button
                  onClick={() =>
                    setCompanyFilter(companyFilter === c.id ? "" : c.id)
                  }
                  data-testid={`company-filter-${c.id}`}
                  className={`w-10 h-10 rounded-xl grid place-items-center text-white transition-colors ${
                    companyFilter === c.id ? "bg-orange-700" : "bg-slate-900"
                  }`}
                  title="Kullanıcılarını filtrele"
                >
                  <Building2 className="w-5 h-5" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-slate-900 truncate flex items-center gap-2">
                    {c.name}
                    <span
                      className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-semibold ${badgeTone}`}
                      data-testid={`sub-badge-${c.id}`}
                    >
                      {badgeLabel}
                    </span>
                  </div>
                  <div className="text-xs text-stone-500 truncate">
                    {dateTR(c.created_at)}
                    {sub.status === "active" && sub.valid_until && (
                      <> · Yenileme: {dateTR(sub.valid_until)}</>
                    )}
                    {sub.status === "trial" && sub.trial_end && (
                      <> · Deneme bitiş: {dateTR(sub.trial_end)}</>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-stone-600 num-tabular">
                  <span title="Kullanıcı">
                    <UsersIcon className="w-3.5 h-3.5 inline mr-1" />
                    {c.user_count}
                    <span className="text-stone-400"> ({c.active_user_count} aktif)</span>
                  </span>
                  <span title="Araç">
                    <CarFront className="w-3.5 h-3.5 inline mr-1" />
                    {c.vehicle_count}
                    {sub.vehicle_limit != null && (
                      <span className="text-stone-400">/{sub.vehicle_limit}</span>
                    )}
                  </span>
                  <span title="Kiralama">
                    <KeyRound className="w-3.5 h-3.5 inline mr-1" />
                    {c.rental_count}
                  </span>
                </div>
                {c.id !== user?.company_id && (
                  <button
                    onClick={() => removeCompany(c)}
                    data-testid={`company-del-${c.id}`}
                    className="h-9 w-9 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50 grid place-items-center"
                    title="Firmayı sil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Users */}
      <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 bg-stone-50/60 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <UsersIcon className="w-4 h-4 text-stone-500" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-stone-500">
              Kullanıcılar
              {companyFilter && (
                <span className="ml-2 text-orange-700 normal-case tracking-normal font-medium">
                  · Firmaya göre filtrelendi{" "}
                  <button
                    onClick={() => setCompanyFilter("")}
                    className="underline"
                  >
                    temizle
                  </button>
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 h-9 rounded-full border border-stone-200 bg-white">
            <Search className="w-4 h-4 text-stone-400" />
            <input
              data-testid="portal-user-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ad, e-posta, firma ara…"
              className="bg-transparent outline-none text-sm w-52"
            />
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-stone-500">Kullanıcı yok.</div>
        ) : (
          <div className="divide-y divide-stone-100">
            {filtered.map((u) => {
              const disabled = u.status === "disabled";
              const isMe = u.id === user?.id;
              return (
                <div
                  key={u.id}
                  data-testid={`portal-user-${u.id}`}
                  className={`px-5 py-4 flex flex-wrap items-center gap-3 ${
                    disabled ? "opacity-60" : ""
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-slate-900 text-white grid place-items-center font-display font-black">
                    {(u.name || u.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                      {u.name || u.email}
                      {isMe && (
                        <span className="text-[10px] uppercase tracking-widest text-orange-700">
                          Siz
                        </span>
                      )}
                      {disabled && (
                        <span className="text-[10px] uppercase tracking-widest bg-stone-200 text-stone-700 rounded-full px-2 py-0.5">
                          Devre Dışı
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 truncate">
                      {u.email} · {u.company_name}
                    </div>
                  </div>
                  <select
                    value={u.role}
                    onChange={(e) => setRole(u, e.target.value)}
                    disabled={isMe}
                    data-testid={`portal-role-${u.id}`}
                    className="h-9 px-3 rounded-full border border-stone-300 text-xs font-semibold bg-white disabled:opacity-50"
                  >
                    <option value="member">Üye</option>
                    <option value="admin">Yönetici</option>
                  </select>
                  {!isMe && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setResetTarget(u)}
                        data-testid={`portal-reset-${u.id}`}
                        className="h-9 px-3 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50 text-sm font-medium flex items-center gap-1.5"
                        title="Şifre sıfırla"
                      >
                        <KeyRound className="w-4 h-4" />
                        Şifre
                      </button>
                      <button
                        onClick={() => toggleStatus(u)}
                        data-testid={`portal-toggle-${u.id}`}
                        className={`h-9 px-3 rounded-full border text-sm font-medium flex items-center gap-1.5 ${
                          disabled
                            ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                            : "border-amber-200 text-amber-800 hover:bg-amber-50"
                        }`}
                        title={disabled ? "Etkinleştir" : "Devre dışı bırak"}
                      >
                        <Power className="w-4 h-4" />
                        {disabled ? "Etkinleştir" : "Devre Dışı"}
                      </button>
                      <button
                        onClick={() => removeUser(u)}
                        data-testid={`portal-del-${u.id}`}
                        className="h-9 w-9 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50 grid place-items-center"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {resetTarget && (
        <ResetDialog
          user={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={load}
        />
      )}
    </div>
  );
}
